import React, { useState } from "react";
import Box from "@mui/material/Box";
import { MuiOtpInput } from "mui-one-time-password-input";
import { useNavigate, useSearchParams } from "react-router-dom";
import { forgetPassword, verifyPassword } from "../../api/userAuth";
import { useNotify } from "../../context/NotifyContext";
import AuthShell from "../../components/auth/AuthShell";
import {
  AuthField,
  authOtpFieldProps,
  authOtpSx,
} from "../../components/auth/AuthField";

const ResetPassword = () => {
  const { notify } = useNotify();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendOtp = async () => {
    if (!username.trim() || !aadhaar.trim()) {
      notify("Please fill Username & Aadhaar", "warning");
      return;
    }
    if (!/^\d{12}$/.test(aadhaar)) {
      notify("Aadhaar must be 12 digits", "warning");
      return;
    }

    try {
      setLoading(true);
      const res = await forgetPassword({ userName: username, aadhaar });
      if (!res.success) return notify(res.message, "error");

      notify("OTP sent successfully", "success");
      setOtpSent(true);
    } catch {
      notify("Failed to send OTP", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOtp = async () => {
    if (otp.length !== 6) return notify("OTP must be 6 digits", "warning");

    try {
      setLoading(true);
      const res = await verifyPassword({ userName: username, otp });
      if (!res.success) return notify(res.message, "error");

      const token = res.data?.resetToken;

      if (from === "login") {
        navigate("/new-mpin", { state: { username, resetToken: token } });
      } else {
        navigate("/new-password", { state: { username, resetToken: token } });
      }
    } catch {
      notify("Invalid OTP", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Account recovery"
      title="Forgot Password"
      subtitle="Verify your username and Aadhaar to reset credentials."
      footer={
        <button type="button" className="ds-auth-link" onClick={() => navigate("/login")}>
          Back to Login
        </button>
      }
    >
      <Box className="flex flex-col gap-3.5">
        <AuthField label="Username" required>
          <input
            className="ds-input"
            placeholder="Enter Username"
            disabled={otpSent}
            value={username}
            onChange={(e) => setUsername(e.target.value.toUpperCase())}
            style={{ textTransform: "uppercase" }}
          />
        </AuthField>

        <AuthField label="Aadhaar Number" required>
          <input
            className="ds-input"
            placeholder="************"
            disabled={otpSent}
            value={aadhaar}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              if (value.length <= 12) setAadhaar(value);
            }}
            inputMode="numeric"
            maxLength={12}
          />
        </AuthField>

        {!otpSent ? (
          <button
            type="button"
            onClick={handleSendOtp}
            className="ds-btn ds-btn-primary ds-btn-block"
            disabled={loading}
          >
            {loading ? "Sending..." : "Send OTP"}
          </button>
        ) : (
          <>
            <AuthField label="Enter OTP" required>
              <MuiOtpInput
                length={6}
                value={otp}
                onChange={setOtp}
                sx={authOtpSx}
                TextFieldsProps={authOtpFieldProps}
              />
            </AuthField>

            <div className="ds-auth-actions">
              <button
                type="button"
                className="ds-btn ds-btn-secondary"
                onClick={handleSendOtp}
                disabled={loading}
              >
                Resend OTP
              </button>
              <button
                type="button"
                className="ds-btn ds-btn-primary"
                disabled={otp.length !== 6 || loading}
                onClick={handleSubmitOtp}
              >
                Verify
              </button>
            </div>
          </>
        )}
      </Box>
    </AuthShell>
  );
};

export default ResetPassword;
